import { type Ref, computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'

import type { Facet, UiPriceFacet } from '../types'

export function useTopFilters({
  priceFacet,
  facets,
  price,
  filters,
  sortOptions,
  sort,
}: {
  price: Ref<[number, number] | undefined>
  filters: Ref<Record<string, string[]>>
  facets: Ref<Facet[]>
  priceFacet?: Ref<UiPriceFacet | undefined>
  sortOptions: Ref<
    {
      label: string
      value: string
      title: string
    }[]
  >
  sort: Ref<string>
}) {
  const i18n = useI18n()
  const sortedFilters = ref<string[]>([])
  const smScrollContainer = ref()
  const lgScrollContainer = ref()
  const breakpoint = useClientOnlyComputedBreakpoint()

  const allFilters = computed<{
    [key: string]: { value: string[]; facet: Facet | UiPriceFacet }
  }>(() => ({
    ...(priceFacet?.value
      ? {
          price: {
            facet: priceFacet.value,
            value: price.value
              ? [
                  price.value
                    .map(
                      (priceValue: number) =>
                        i18n.price(priceValue, {
                          maximumFractionDigits: 0,
                        }) || 0,
                    )
                    .join(' - '),
                ]
              : [],
          },
        }
      : {}),
    ...facets.value.reduce(
      (a, facet) => ({
        ...a,
        [facet.name]: {
          facet,
          value: filters.value[facet.name],
        },
      }),
      {},
    ),
  }))

  const scrollToLeft = () => {
    const scrollElement =
      breakpoint.value < Breakpoint.LG
        ? smScrollContainer.value
        : lgScrollContainer.value
    scrollElement?.scrollTo({
      left: 0,
      behavior: 'smooth',
    })
  }

  function applyFiltersSort() {
    sortedFilters.value = Object.values(allFilters.value)
      .filter((f, i) => i < 5 || f.value?.length)
      .sort((a, b) => {
        if (!!a.value?.length === !!b.value?.length) return 0

        return a.value?.length ? -1 : 1
      })
      .map((filter) => filter.facet.name)
    scrollToLeft()
  }

  const sortValue = computed(() =>
    sortOptions.value.find((i) => i.value === sort.value),
  )

  function getLabel(filterName: string) {
    const value = allFilters.value[filterName].facet.values.find((val) => {
      return (
        typeof val !== 'number' &&
        val.name === allFilters.value[filterName].value[0]
      )
    })

    return typeof value !== 'number' ? (value?.label ?? '') : ''
  }

  return {
    allFilters,
    sortedFilters,
    applyFiltersSort,
    smScrollContainer,
    lgScrollContainer,
    sortValue,
    getLabel,
  }
}
